import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { fetchMedications, fetchSurgeries, fetchLegalContent, fetchDocumentResources, fetchFormResources, fetchQuestionnaire, fetchClinics, fetchAdminUsers, fetchAdminTransactions, fetchAdminDashboardStats, fetchBAADocuments, fetchFaqData } from '../api/adminServices';
import { getToken } from '../utils/storage';
import { useAuth } from './AuthContext';

// Define the shape of a State object based on observed usage
export interface StateItem {
    name: string;
    code: string;
    [key: string]: any;
}

export interface LookupItem {
    id: number;
    lookup_type: string;
    label: string;
    value: string;
    sort_order: number;
}

export interface MedicationItem {
    id: number;
    med_type: 'MEDICATION' | 'FREQUENCY' | 'SIDE_EFFECT';
    value: string;
}

export type MedicationGroup = {
    MEDICATION: MedicationItem[];
    FREQUENCY: MedicationItem[];
    SIDE_EFFECT: MedicationItem[];
};

export interface SurgeryItem {
    id: number;
    surgery_type: 'SURGERY_TYPE' | 'BODY_PART' | 'SURGERY_SUB_TYPE' | 'FOLLOW_UP' | 'STATUS';
    value: string;
    parent?: number | null;
    is_disabled?: boolean;
}


export type SurgeryGroup = {
    SURGERY_TYPE: SurgeryItem[];
    BODY_PART: SurgeryItem[];
    SURGERY_SUB_TYPE: SurgeryItem[];
    FOLLOW_UP: SurgeryItem[];
    STATUS: SurgeryItem[];
};


export interface DocumentResource {
    id: number;
    title: string;
    category: string;
    resource_file: string | null;
    link_url: string | null;
    uploaded_at?: string;
}

export interface LegalItem {
    id: number;
    section_type: string;
    title: string;
    content: string;
}

export interface QuestionItem {
    id: number;
    text: string;
    answer?: string;
    additionalComment?: string;
    options?: string[];
    subQuestions?: QuestionItem[];
    // Backwards compatibility for the RecursiveQuestionSerializer field mapping
    parentId?: number | null;
    is_disabled?: boolean;
}

export interface ClinicOffice {
    id: string | number;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    phone_number: string;
}

export interface TransactionItem {
    id: string | number;
    username: string;
    email: string;
    clinic_name: string;
    amount: string;
    currency: string;
    status: string;
    paid_at: string;
    created_at: string;
    stripe_invoice_id: string;
    metadata?: any;
}

export interface ClinicItem {
    id: number | string;
    name: string;
    email: string;
    licence_number?: string;
    status?: string;
    is_active?: boolean;
    offices?: ClinicOffice[];
    transactions?: TransactionItem[];
    certificate_count?: number;
    certificate_count_today?: number;
    revenue?: number;
}

export interface AdminUserItem {
    id: number | string;
    username: string;
    email: string;
    is_disabled: boolean;
    roles: string[];
}

export interface DashboardStats {
    total_users: number;
    active_clinics: number;
    total_invoices: number;
    downloads: number;
    total_revenue: number;
    pending_actions: number;
}

export interface DashboardData {
    stats: DashboardStats;
    recent_transactions: TransactionItem[];
}



interface AdminContextType {
    states: StateItem[];
    lookups: LookupItem[];
    medications: MedicationItem[];
    surgeries: SurgeryItem[];
    legalContent: LegalItem[];
    documentResources: DocumentResource[];
    formResources: DocumentResource[];
    baaDocuments: DocumentResource[];
    loadingBAA: boolean;
    loadBAADocuments: (force?: boolean) => Promise<void>;
    refreshBAADocuments: () => Promise<void>;
    faqs: any[];
    loadingFaqs: boolean;
    loadFaqs: (force?: boolean) => Promise<void>;
    refreshFaqs: () => Promise<void>;
    questionnaire: QuestionItem[];
    loadingLegal: boolean;
    loadingStates: boolean;
    loadingLookups: boolean;
    loadingMedications: boolean;
    loadingSurgeries: boolean;
    loadingDocumentResources: boolean;
    loadingFormResources: boolean;
    loadingQuestionnaire: boolean;

    getLookupsByType: (type: string) => LookupItem[];
    getMedicationsByType: (type: 'MEDICATION' | 'FREQUENCY' | 'SIDE_EFFECT') => MedicationItem[];
    getSurgeriesByType: (type: 'SURGERY_TYPE' | 'BODY_PART' | 'SURGERY_SUB_TYPE' | 'FOLLOW_UP' | 'STATUS', includeDisabled?: boolean) => SurgeryItem[];


    loadLegalContent: (force?: boolean) => Promise<void>;
    loadDocumentResources: (force?: boolean) => Promise<void>;
    loadFormResources: (force?: boolean) => Promise<void>;
    loadMedications: (force?: boolean) => Promise<void>;
    loadSurgeries: (force?: boolean) => Promise<void>;
    loadQuestionnaire: (force?: boolean) => Promise<void>;
    loadClinics: (force?: boolean) => Promise<void>;
    loadUsers: (force?: boolean) => Promise<void>;
    loadTransactions: (force?: boolean) => Promise<void>;
    loadDashboardData: (force?: boolean) => Promise<void>;

    refreshDocumentResources: () => Promise<void>;
    refreshFormResources: () => Promise<void>;
    refreshMedications: () => Promise<void>;
    refreshSurgeries: () => Promise<void>;
    refreshQuestionnaire: () => Promise<void>;
    refreshClinics: () => Promise<void>;
    searchMedications: (type: 'MEDICATION' | 'FREQUENCY' | 'SIDE_EFFECT', search: string) => Promise<MedicationItem[]>;
    searchSurgeries: (type: 'SURGERY_TYPE' | 'BODY_PART' | 'SURGERY_SUB_TYPE' | 'FOLLOW_UP' | 'STATUS', search: string, includeDisabled?: boolean) => Promise<SurgeryItem[]>;

    clinics: ClinicItem[];

    loadingClinics: boolean;
    users: AdminUserItem[];
    loadingUsers: boolean;
    refreshUsers: () => Promise<void>;
    transactions: TransactionItem[];
    loadingTransactions: boolean;
    refreshTransactions: () => Promise<void>;
    dashboardData: DashboardData | null;
    loadingDashboard: boolean;
    refreshDashboard: () => Promise<void>;
}



const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [states, setStates] = useState<StateItem[]>([]);
    const [lookups, setLookups] = useState<LookupItem[]>([]);
    const [medications, setMedications] = useState<MedicationItem[]>([]);
    const [legalContent, setLegalContent] = useState<LegalItem[]>([]);
    const [documentResources, setDocumentResources] = useState<DocumentResource[]>([]);
    const [formResources, setFormResources] = useState<DocumentResource[]>([]);
    const [baaDocuments, setBaaDocuments] = useState<DocumentResource[]>([]);
    const [faqs, setFaqs] = useState<any[]>([]);
    const [questionnaire, setQuestionnaire] = useState<QuestionItem[]>([]);
    const [loadingBAA, setLoadingBAA] = useState(false);
    const [loadingFaqs, setLoadingFaqs] = useState(false);
    const [loadingLegal, setLoadingLegal] = useState(true);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingLookups, setLoadingLookups] = useState(false);
    const [loadingMedications, setLoadingMedications] = useState(true);
    const [loadingDocumentResources, setLoadingDocumentResources] = useState(true);
    const [loadingFormResources, setLoadingFormResources] = useState(true);
    const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(true);
    const [clinics, setClinics] = useState<ClinicItem[]>([]);
    const [loadingClinics, setLoadingClinics] = useState(true);
    const [users, setUsers] = useState<AdminUserItem[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [transactions, setTransactions] = useState<TransactionItem[]>([]);
    const [loadingTransactions, setLoadingTransactions] = useState(true);
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loadingDashboard, setLoadingDashboard] = useState(true);
    const [surgeries, setSurgeries] = useState<SurgeryItem[]>([]);
    const [loadingSurgeries, setLoadingSurgeries] = useState(true);


    // Use a ref to track active fetches and prevent redundant simultaneous calls
    const activeFetches = useRef<Record<string, boolean>>({});


    const [medicationsGrouped, setMedicationsGrouped] = useState<MedicationGroup>({
        MEDICATION: [],
        FREQUENCY: [],
        SIDE_EFFECT: []
    });

    const [surgeriesGrouped, setSurgeriesGrouped] = useState<SurgeryGroup>({
        SURGERY_TYPE: [],
        BODY_PART: [],
        SURGERY_SUB_TYPE: [],
        FOLLOW_UP: [],
        STATUS: []
    });


    const { isAuthenticated, user } = useAuth();
    const isAdmin = useMemo(() => {
        const roles = user?.roles || [];
        const hasAdmin = roles.some((role: any) => role.name?.toLowerCase() === 'admin');
        const hasOthers = roles.some((role: any) => role.name?.toLowerCase() !== 'admin');
        return hasAdmin && !hasOthers;
    }, [user]);

    const loadDocumentResources = useCallback(async (force = false) => {
        if (!force && documentResources.length > 0) return;
        if (activeFetches.current.document) return;

        activeFetches.current.document = true;
        setLoadingDocumentResources(true);
        try {
            const data = await fetchDocumentResources();
            if (Array.isArray(data)) {
                setDocumentResources(data);
            } else if (data && Array.isArray(data.results)) {
                setDocumentResources(data.results);
            } else {
                setDocumentResources([]);
            }
        } catch (error) {
            console.error("Failed to fetch document resources", error);
        } finally {
            setLoadingDocumentResources(false);
            activeFetches.current.document = false;
        }
    }, [documentResources.length]);

    const loadFormResources = useCallback(async (force = false) => {
        if (!force && formResources.length > 0) return;
        if (activeFetches.current.forms) return;

        activeFetches.current.forms = true;
        setLoadingFormResources(true);
        try {
            const data = await fetchFormResources();
            if (Array.isArray(data)) {
                setFormResources(data);
            } else if (data && Array.isArray(data.results)) {
                setFormResources(data.results);
            } else {
                setFormResources([]);
            }
        } catch (error) {
            console.error("Failed to fetch form resources", error);
        } finally {
            setLoadingFormResources(false);
            activeFetches.current.forms = false;
        }
    }, [formResources.length]);

    const loadQuestionnaire = useCallback(async (force = false) => {
        if (!force && questionnaire.length > 0) return;
        if (activeFetches.current.questionnaire) return;

        activeFetches.current.questionnaire = true;
        setLoadingQuestionnaire(true);
        try {
            const data = await fetchQuestionnaire();
            // The API now returns root questions with subQuestions nested
            setQuestionnaire(Array.isArray(data) ? data : (data?.results || []));
        } catch (error) {
            console.error("Failed to fetch questionnaire", error);
        } finally {
            setLoadingQuestionnaire(false);
            activeFetches.current.questionnaire = false;
        }
    }, [questionnaire.length]);

    const loadClinics = useCallback(async (force = false) => {
        if (!isAdmin) {
            setLoadingClinics(false);
            return;
        }
        if (!force && clinics.length > 0) return;
        if (activeFetches.current.clinics) return;

        activeFetches.current.clinics = true;
        setLoadingClinics(true);
        try {
            const data = await fetchClinics({ page_size: 1000 });
            setClinics(Array.isArray(data) ? data : (data?.results || []));
        } catch (error) {
            console.error("Failed to fetch clinics", error);
        } finally {
            setLoadingClinics(false);
            activeFetches.current.clinics = false;
        }
    }, [clinics.length, isAdmin]);

    const loadUsers = useCallback(async (force = false) => {
        if (!isAdmin) {
            setLoadingUsers(false);
            return;
        }
        if (!force && users.length > 0) return;
        if (activeFetches.current.users) return;

        activeFetches.current.users = true;
        setLoadingUsers(true);
        try {
            const data = await fetchAdminUsers({ page_size: 1000 });
            setUsers(Array.isArray(data) ? data : (data?.results || []));
        } catch (error) {
            console.error("Failed to fetch users", error);
        } finally {
            setLoadingUsers(false);
            activeFetches.current.users = false;
        }
    }, [users.length, isAdmin]);

    const loadTransactions = useCallback(async (force = false) => {
        if (!isAdmin) {
            setLoadingTransactions(false);
            return;
        }
        if (!force && transactions.length > 0) return;
        if (activeFetches.current.transactions) return;

        activeFetches.current.transactions = true;
        setLoadingTransactions(true);
        try {
            const data = await fetchAdminTransactions({ page_size: 1000 });
            setTransactions(Array.isArray(data) ? data : (data?.results || []));
        } catch (error) {
            console.error("Failed to fetch transactions", error);
        } finally {
            setLoadingTransactions(false);
            activeFetches.current.transactions = false;
        }
    }, [transactions.length, isAdmin]);

    const loadDashboardData = useCallback(async (force = false) => {
        if (!isAdmin) {
            setLoadingDashboard(false);
            return;
        }
        if (!force && dashboardData) return;
        if (activeFetches.current.dashboard) return;

        activeFetches.current.dashboard = true;
        setLoadingDashboard(true);
        try {
            const data = await fetchAdminDashboardStats();
            setDashboardData(data);
        } catch (error) {
            console.error("Failed to fetch dashboard stats", error);
        } finally {
            setLoadingDashboard(false);
            activeFetches.current.dashboard = false;
        }
    }, [dashboardData, isAdmin]);

    const loadLegalContent = useCallback(async (force = false) => {
        if (!force && legalContent.length > 0) return;
        if (activeFetches.current.legal) return;

        activeFetches.current.legal = true;
        setLoadingLegal(true);
        try {
            const legalData = await fetchLegalContent();
            setLegalContent(Array.isArray(legalData) ? legalData : []);
        } catch (e) {
            console.error("Failed to fetch legal content", e);
        } finally {
            setLoadingLegal(false);
            activeFetches.current.legal = false;
        }
    }, [legalContent.length]);


    const loadBAADocuments = useCallback(async (force = false) => {
        if (!force && baaDocuments.length > 0) return;
        if (activeFetches.current.baa) return;

        activeFetches.current.baa = true;
        setLoadingBAA(true);
        try {
            const data = await fetchBAADocuments();
            setBaaDocuments(Array.isArray(data) ? data : (data.results || []));
        } catch (error) {
            console.error("Failed to fetch BAA documents", error);
        } finally {
            setLoadingBAA(false);
            activeFetches.current.baa = false;
        }
    }, [baaDocuments.length]);

    const refreshBAADocuments = useCallback(async () => {
        await loadBAADocuments(true);
    }, [loadBAADocuments]);

    const loadFaqs = useCallback(async (force = false) => {
        if (!force && faqs.length > 0) return;
        if (activeFetches.current.faqs) return;

        activeFetches.current.faqs = true;
        setLoadingFaqs(true);
        try {
            const data = await fetchFaqData();
            setFaqs(Array.isArray(data) ? data : (data.results || []));
        } catch (error) {
            console.error("Failed to fetch FAQs", error);
        } finally {
            setLoadingFaqs(false);
            activeFetches.current.faqs = false;
        }
    }, [faqs.length]);

    const refreshFaqs = useCallback(async () => {
        await loadFaqs(true);
    }, [loadFaqs]);

    const loadMedications = useCallback(async (force = false) => {
        if (!force && medications.length > 0) return;
        if (activeFetches.current.medications) return;

        activeFetches.current.medications = true;
        setLoadingMedications(true);
        try {
            const data = await fetchMedications({ page_size: 5000 });
            const results = Array.isArray(data) ? data : (data?.results || []);

            const grouped: MedicationGroup = {
                MEDICATION: [],
                FREQUENCY: [],
                SIDE_EFFECT: []
            };

            results.forEach((item: MedicationItem) => {
                if (grouped[item.med_type]) {
                    grouped[item.med_type].push(item);
                }
            });

            setMedicationsGrouped(grouped);
            setMedications(results);
        } catch (error) {
            console.error("Failed to fetch medications", error);
        } finally {
            setLoadingMedications(false);
            activeFetches.current.medications = false;
        }
    }, [medications.length]);


    const loadSurgeries = useCallback(async (force = false) => {
        if (!force && surgeries.length > 0) return;
        if (activeFetches.current.surgeries) return;

        activeFetches.current.surgeries = true;
        setLoadingSurgeries(true);
        try {
            const data = await fetchSurgeries({ page_size: 5000 });
            const results = Array.isArray(data) ? data : (data?.results || []);

            const grouped: SurgeryGroup = {
                SURGERY_TYPE: [],
                BODY_PART: [],
                SURGERY_SUB_TYPE: [],
                FOLLOW_UP: [],
                STATUS: []
            };

            results.forEach((item: SurgeryItem) => {
                if (grouped[item.surgery_type]) {
                    grouped[item.surgery_type].push(item);
                }
            });

            setSurgeriesGrouped(grouped);
            setSurgeries(results);
        } catch (error) {
            console.error("Failed to fetch surgeries", error);
        } finally {
            setLoadingSurgeries(false);
            activeFetches.current.surgeries = false;
        }
    }, [surgeries.length]);



    useEffect(() => {
        if (!isAuthenticated) {
            // Clear state when logged out
            setMedicationsGrouped({ MEDICATION: [], FREQUENCY: [], SIDE_EFFECT: [] });
            setSurgeries([]);
            setSurgeriesGrouped({ SURGERY_TYPE: [], BODY_PART: [], SURGERY_SUB_TYPE: [], FOLLOW_UP: [], STATUS: [] });
            setLegalContent([]);
            setDocumentResources([]);
            setFormResources([]);
            setQuestionnaire([]);
            setClinics([]);
            setUsers([]);
            setTransactions([]);
            setDashboardData(null);

            setLoadingMedications(false);
            setLoadingSurgeries(false);
            setLoadingLegal(false);
            setLoadingDocumentResources(false);
            setLoadingFormResources(false);
            setLoadingQuestionnaire(false);
            setLoadingClinics(false);
            setLoadingUsers(false);
            setLoadingDashboard(false);
            setLoadingStates(false);


            // Reset active fetches
            activeFetches.current = {};
        } else if (!isAdmin) {
            // If logged in but not an admin, clear admin-only loading states
            setLoadingClinics(false);
            setLoadingUsers(false);
            setLoadingTransactions(false);
            setLoadingDashboard(false);
        }
    }, [isAuthenticated, isAdmin]);

    const getLookupsByType = useCallback((type: string) => {
        return lookups
            .filter(item => item.lookup_type === type)
            .sort((a, b) => a.sort_order - b.sort_order);
    }, [lookups]);

    const getMedicationsByType = useCallback((type: 'MEDICATION' | 'FREQUENCY' | 'SIDE_EFFECT') => {
        return medicationsGrouped[type] || [];
    }, [medicationsGrouped]);

    const getSurgeriesByType = useCallback((type: keyof SurgeryGroup, includeDisabled = false) => {
        const list = surgeriesGrouped[type] || [];
        if (includeDisabled) return list;
        return list.filter(item => !item.is_disabled);
    }, [surgeriesGrouped]);


    const refreshDocumentResources = useCallback(async () => {
        await loadDocumentResources(true);
    }, [loadDocumentResources]);

    const refreshFormResources = useCallback(async () => {
        await loadFormResources(true);
    }, [loadFormResources]);

    const refreshMedications = useCallback(async () => {
        await loadMedications(true);
    }, [loadMedications]);

    const refreshSurgeries = useCallback(async () => {
        await loadSurgeries(true);
    }, [loadSurgeries]);


    const refreshQuestionnaire = useCallback(async () => {
        await loadQuestionnaire(true);
    }, [loadQuestionnaire]);

    const refreshClinics = useCallback(async () => {
        await loadClinics(true);
    }, [loadClinics]);

    const refreshUsers = useCallback(async () => {
        await loadUsers(true);
    }, [loadUsers]);

    const refreshTransactions = useCallback(async () => {
        await loadTransactions(true);
    }, [loadTransactions]);

    const refreshDashboard = useCallback(async () => {
        await loadDashboardData(true);
    }, [loadDashboardData]);



    const searchMedications = async (type: 'MEDICATION' | 'FREQUENCY' | 'SIDE_EFFECT', search: string): Promise<MedicationItem[]> => {
        try {
            const data = await fetchMedications({ type, search, page_size: 50 });
            return Array.isArray(data) ? data : (data?.results || []);
        } catch (error) {
            console.error("Failed to search medications", error);
            return [];
        }
    };

    const searchSurgeries = useCallback(async (type: keyof SurgeryGroup, search: string, includeDisabled = false) => {
        try {
            const data = await fetchSurgeries({ type, search, page_size: 100 });
            const results = Array.isArray(data) ? data : (data?.results || []);
            if (includeDisabled) return results;
            return results.filter((item: any) => !item.is_disabled);
        } catch (error) {
            console.error("Search failed", error);
            return [];
        }
    }, []);


    const contextValue = useMemo(() => ({
        states,
        lookups,
        legalContent,
        medications,
        surgeries,
        documentResources,
        formResources,
        questionnaire,
        loadingStates,
        loadingLegal,
        loadingLookups,
        loadingMedications,
        loadingSurgeries,
        loadingDocumentResources,
        loadingFormResources,
        loadingQuestionnaire,
        loadingClinics,
        clinics,
        getLookupsByType,
        getMedicationsByType,
        getSurgeriesByType,
        loadLegalContent,
        loadDocumentResources,
        loadFormResources,
        loadMedications,
        loadSurgeries,
        loadQuestionnaire,
        loadClinics,
        loadUsers,
        loadTransactions,
        loadDashboardData,
        refreshDocumentResources,
        refreshFormResources,
        refreshMedications,
        refreshSurgeries,
        refreshQuestionnaire,
        refreshClinics,
        searchMedications,
        searchSurgeries,
        users,
        loadingUsers,
        refreshUsers,
        baaDocuments,
        loadingBAA,
        loadBAADocuments,
        refreshBAADocuments,
        transactions,
        loadingTransactions,
        refreshTransactions,
        dashboardData,
        loadingDashboard,
        refreshDashboard,
        faqs,
        loadingFaqs,
        loadFaqs,
        refreshFaqs
    }), [
        states, lookups, legalContent, medications, surgeries, documentResources, formResources, questionnaire,
        loadingStates, loadingLegal, loadingLookups, loadingMedications, loadingSurgeries, loadingDocumentResources,
        loadingFormResources, loadingQuestionnaire, loadingClinics, clinics, getLookupsByType,
        getMedicationsByType, getSurgeriesByType, loadLegalContent, loadDocumentResources, loadFormResources,
        loadMedications, loadSurgeries, loadQuestionnaire, loadClinics, loadUsers, loadTransactions,
        loadDashboardData, refreshDocumentResources, refreshFormResources, refreshMedications, refreshSurgeries,
        refreshQuestionnaire, refreshClinics, searchMedications, searchSurgeries, users, loadingUsers,
        refreshUsers, transactions, loadingTransactions, refreshTransactions, dashboardData,
        loadingDashboard, refreshDashboard, baaDocuments, loadingBAA, loadBAADocuments, refreshBAADocuments,
        faqs, loadingFaqs, loadFaqs, refreshFaqs
    ]);


    return (
        <AdminContext.Provider value={contextValue}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};

